/* eslint-disable no-restricted-imports */
// This is the ONLY place where we import React Query hooks directly
// Everywhere else in the codebase, we use the generators defined here
// to override the default RQ behavior
import {
  QueryKey,
  UseInfiniteQueryOptions,
  useInfiniteQuery as useInfiniteRQQuery,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  useMutation as useRQMutation,
  useQueries as useRQQueries,
  useQuery as useRQQuery,
} from "@tanstack/react-query";

import { getVerboseError } from "@shared/libs/oh-errors/error-formatter";
import { OhApi } from "@shared/libs/siayuda";
import { toastApi } from "@shared/libs/toasts";

import { listQueryKey } from "./query-keys.constants";
import { rerouteApiError } from "./query.helpers";
import {
  InfiniteQueryProps,
  PaginatedQueryParams,
  PaginatedQueryResponse,
  QueryResultWithKey,
} from "./query.type";

/**
 * an overload of react-query's useQuery hook which add specific logic
 * related to how we handle errors in HB
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call
 * @returns an overloaded react query useQuery hook
 */
export const queryGenerator =
  <T extends readonly unknown[], Data, Err = OhApi.Failure>(
    queryBaseName: string | string[],
    requestFn: (...args: T) => Promise<OhApi.ApiResponse<Data>>
  ) =>
  (
    data: T,
    options?: UseQueryOptions<Data, Err>
  ): QueryResultWithKey<UseQueryResult<Data, Err>> => {
    // construct query key beforehand to expose it
    const queryKey = [...(Array.isArray(queryBaseName) ? queryBaseName : [queryBaseName]), data];
    return {
      queryKey,
      ...useRQQuery<Data, Err>(
        queryKey,
        () => rerouteApiError(requestFn(...data)).then(r => r.value),
        options
      ),
    };
  };

/**
 * an overload of react-query's useQueries hook which add specific logic
 * related to how we handle errors in HB
 * BEHOLD: contrary to standard useQueries, only allow multiple fetch of same data type
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call
 * @returns an overloaded react query useQuery hook
 */
export const queriesGenerator =
  <T extends readonly unknown[], Data, Err = OhApi.Failure>(
    queryBaseName: string | string[],
    requestFn: (...args: T) => Promise<OhApi.ApiResponse<Data>>
  ) =>
  ( data: T[],
    options?: UseQueryOptions<Data, Err>
  ) =>
    useRQQueries({
      queries: data.map(queryData => ({
        queryKey: [...(Array.isArray(queryBaseName) ? queryBaseName : [queryBaseName]), queryData],
        queryFn: () => rerouteApiError(requestFn(...queryData)).then(r => r.value),
        ...options,
      })),
    }) as UseQueryResult<Data, Err>[];

// global toggle for mutation notifications related to global errors
let IS_AUTOMATIC_GLOBAL_ERROR_ENABLE = true;
export const enableAutomaticGlobalError = () => (IS_AUTOMATIC_GLOBAL_ERROR_ENABLE = true);
export const disableAutomaticGlobalError = () => (IS_AUTOMATIC_GLOBAL_ERROR_ENABLE = false);

/**
 * an overload of react-query's useMutation hook which add specific logic
 * related to how we handle errors in HB
 * @param requestFn function which triggers our edition through network call
 * @returns an overloaded react query useMutation hook
 */
export const mutationGenerator =
  <T extends readonly unknown[], Data, Err extends OhApi.Failure = OhApi.Failure>(
    requestFn: (...args: T) => Promise<OhApi.ApiResponse<Data>>
  ) =>
  (options?: UseMutationOptions<Data, Err, T>) => {
    return useRQMutation<Data, Err, T>(
      (data: T) => rerouteApiError(requestFn(...data)).then(r => r.value),
      {
        ...options,
        onError: (err, variables, context) => {
          if (OhApi.isFetchError(err) || OhApi.isTimeoutError(err)) {
            toastApi.add({
              variant: "error",
              duration: 10_000,
              title: "Une erreur de connexion s’est produite",
              message:
                "Cela peut être dû à un délai d'attente ou à une connexion instable. " +
                "Voici quelques solutions : vérifiez votre connexion Internet, rechargez " +
                "la page ou réessayez plus tard. Si le problème persiste contactez-nous.",
            });
          }
          if (OhApi.isGlobalError(err) && IS_AUTOMATIC_GLOBAL_ERROR_ENABLE) {
            toastApi.add({
              variant: "error",
              title: "Une erreur s'est produite",
              message: getVerboseError(err.error, err.extra),
            });
          }
          if (OhApi.isUnrecoverableHttpError(err) && err.statusCode === 403) {
            toastApi.add({
              variant: "error",
              title: "Une erreur s'est produite",
              message: "Vous n'avez pas les droits pour effectuer cette action",
            });
          } else if (OhApi.isUnrecoverableHttpError(err) && err.statusCode === 401) {
            toastApi.add({
              variant: "error",
              title: "Une erreur s'est produite",
              message: "Vous n'êtes pas autorisé à effectuer cette action",
            });
          } else if (OhApi.isUnrecoverableHttpError(err)) {
            toastApi.add({
              variant: "error",
              title: "Une erreur s'est produite",
              message:
                "Cela peut être dû à un problème serveur. " +
                "Voici quelques solutions : vérifiez votre connexion Internet, rechargez " +
                "la page ou réessayez plus tard. Si le problème persiste contactez-nous.",
            });
          }
          options?.onError?.(err, variables, context);
        },
      }
    );
  };

/**
 * an overload of react-query's useInfiniteQuery hook which add specific logic
 * related to how we handle errors and paginated query filters in HB
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call which filterType input :
 * - filter
 * - sort
 * - pager
 * @returns an overloaded react query useInfiniteQuery hook
 */
export const infinitePaginatedQueryGenerator =
  <Filters, Datum, Err = OhApi.Failure>(
    queryBaseName: string | string[],
    requestFn: (props: PaginatedQueryParams<Filters>) => Promise<OhApi.ApiResponse<Datum[]>>
  ) =>
  (data: InfiniteQueryProps<Filters>) =>
    useInfiniteRQQuery<PaginatedQueryResponse<Datum>, Err>(
      // keys related to queryBaseName and incoming filters
      [...(Array.isArray(queryBaseName) ? queryBaseName : [queryBaseName]), "list", data],
      ({ pageParam }) =>
        rerouteApiError(
          requestFn({
            // rebuild filter, sort and pager data
            filters: data.filters,
            sort: data.sort,
            pager: { size: data.size, page: pageParam?.page ?? 1 },
          })
        ).then(resp => ({
          // extract pager and data from response
          pager: resp.pager,
          items: resp.value,
        })),
      {
        // retrieve next and previous page data from current response data
        getNextPageParam: ({ pager }) =>
          pager && pager.page !== pager.pages ? { page: pager.page + 1 } : undefined,
        getPreviousPageParam: ({ pager }) =>
          pager && pager.page > 0 ? { page: pager.page - 1 } : undefined,
      }
    );

/**
 * an overload of react-query's useInfiniteQuery hook which add specific logic
 * related to how we handle errors and cursored query filters in HB
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call
 * @returns an overloaded react query useInfiniteQuery hook
 */
type InfiniteCursoredQueryOptions<
  Filters,
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<
  UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
  "queryKey" | "queryFn" | "getNextPageParam" | "getPreviousPageParam"
> & {
  getPreviousPageParam?: (data: TQueryData) => Filters | undefined;
  getNextPageParam?: (data: TQueryData) => Filters | undefined;
};

export const infiniteCursoredQueryGenerator =
  <Filters extends any[], Datum, Err = OhApi.Failure>(
    queryBaseName: string | string[],
    requestFn: (...props: Filters) => Promise<OhApi.ApiResponse<Datum[]>>
  ) =>
  (
    initialFilters: Filters,
    options?: InfiniteCursoredQueryOptions<Filters, Datum[], Err, Datum[]>
  ) => {
    const queryKey = [
      ...(Array.isArray(queryBaseName) ? queryBaseName : [queryBaseName]),
      "list",
      initialFilters,
    ];
    return {
      queryKey,
      ...useInfiniteRQQuery<Datum[], Err>(
        queryKey,
        ({ pageParam }) =>
          rerouteApiError(requestFn(...(pageParam || initialFilters))).then(resp => resp.value),
        options
      ),
    };
  };

/**
 * an overload of react-query's useQuery hook which add specific logic.
 * returned data is structured as { items: In[], pager?: OhAPI.Pager }
 * giving us access to pagination data
 * @param queryBaseName main query key
 * @param requestFn function which triggers our network call with filterType input :
 * - filter
 * - sort
 * - pager
 * @returns an overloaded react query useQuery hook
 */

export const paginatedQueryGenerator =
  <Filters, Datum, Err = OhApi.Failure>(
    queryBaseName: string,
    requestFn: (params: PaginatedQueryParams<Filters>) => Promise<OhApi.ApiResponse<Datum[]>>
  ) =>
  (
    params: PaginatedQueryParams<Filters>,
    options?: UseQueryOptions<PaginatedQueryResponse<Datum>, Err>
  ) =>
    useRQQuery<PaginatedQueryResponse<Datum>, Err>(
      [queryBaseName, listQueryKey, params],
      () =>
        rerouteApiError(requestFn(params)).then(resp => ({
          items: resp.value,
          pager: resp.pager,
        })),
      options
    );
